/**
 * Email Verification Page
 * Verify email address with token from email link
 */

import { useState, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { api } from '../services/api'

export default function EmailVerificationPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token')

  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying')
  const [errorMessage, setErrorMessage] = useState('')

  useEffect(() => {
    if (!token) {
      setStatus('error')
      setErrorMessage('驗證連結無效')
      return
    }

    const verifyEmail = async () => {
      try {
        await api.post('/auth/verify-email', { token })
        setStatus('success')
        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login')
        }, 3000)
      } catch (error) {
        setStatus('error')
        if (error instanceof Error) {
          setErrorMessage(error.message)
        } else {
          setErrorMessage('驗證失敗，請稍後再試')
        }
      }
    }

    verifyEmail()
  }, [token, navigate])

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.title}>CyberLoan.ai</h1>
          <p style={styles.subtitle}>電子郵件驗證</p>
        </div>

        {status === 'verifying' && (
          <div style={styles.verifyingBox}>
            <div style={styles.spinner}></div>
            <p style={styles.verifyingText}>正在驗證您的電子郵件...</p>
          </div>
        )}

        {status === 'success' && (
          <div style={styles.successBox}>
            <span style={styles.successIcon}>✓</span>
            <div>
              <p style={styles.successTitle}>驗證成功！</p>
              <p style={styles.successText}>您的電子郵件已成功驗證。</p>
              <p style={styles.successText}>即將跳轉到登入頁面...</p>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div style={styles.errorBox}>
            <span style={styles.errorIcon}>✕</span>
            <div>
              <p style={styles.errorTitle}>驗證失敗</p>
              <p style={styles.errorText}>{errorMessage}</p>
              <p style={styles.errorText}>驗證連結可能已過期或無效。</p>
            </div>
          </div>
        )}

        <div style={styles.footer}>
          {status === 'error' && (
            <Link to="/login" style={styles.link}>
              返回登入頁面
            </Link>
          )}
          {status === 'success' && (
            <Link to="/login" style={styles.link}>
              立即登入
            </Link>
          )}
        </div>
      </div>
    </div>
  )
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
    padding: '20px',
  },
  card: {
    width: '100%',
    maxWidth: '450px',
    backgroundColor: 'white',
    borderRadius: '10px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    padding: '40px',
  },
  header: {
    textAlign: 'center',
    marginBottom: '30px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#2563eb',
    marginBottom: '8px',
  },
  subtitle: {
    fontSize: '16px',
    color: '#666',
  },
  verifyingBox: {
    padding: '40px 20px',
    textAlign: 'center',
  },
  spinner: {
    width: '40px',
    height: '40px',
    margin: '0 auto 20px',
    border: '4px solid #e5e7eb',
    borderTop: '4px solid #2563eb',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  verifyingText: {
    fontSize: '16px',
    color: '#666',
  },
  successBox: {
    padding: '20px',
    backgroundColor: '#dcfce7',
    borderRadius: '5px',
    color: '#15803d',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '20px',
  },
  successIcon: {
    fontSize: '24px',
    fontWeight: 'bold',
  },
  successTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  successText: {
    fontSize: '14px',
    marginBottom: '4px',
  },
  errorBox: {
    padding: '20px',
    backgroundColor: '#fee2e2',
    borderRadius: '5px',
    color: '#dc2626',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '20px',
  },
  errorIcon: {
    fontSize: '24px',
    fontWeight: 'bold',
  },
  errorTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  errorText: {
    fontSize: '14px',
    marginBottom: '4px',
  },
  link: {
    color: '#2563eb',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500',
  },
  footer: {
    marginTop: '20px',
    textAlign: 'center',
    fontSize: '14px',
  },
}

// Add keyframes animation via style tag
const styleSheet = document.createElement('style')
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`
document.head.appendChild(styleSheet)
